// Pure command-palette logic — the query→ranked-list core, kept free of React so
// it can be unit-tested in isolation and reused by the CommandPalette component.

/** The page a command belongs to, used to surface a page's own actions first. */
export type PageKey =
  | 'home'
  | 'briefing'
  | 'constellation'
  | 'domains'
  | 'springs'
  | 'signals'
  | 'ledger'
  | 'missions'
  | 'agents'
  | 'trove'
  | 'commune'
  | 'settings'
  | 'other'

/** A non-navigation action a command can trigger, resolved by the component. */
export type CommandAction = 'toggle-theme' | 'appearance'

export interface Command {
  /** Stable id — used as the React key and the aria option id. */
  id: string
  /** The label the user reads and searches. */
  title: string
  /** Optional muted subtitle shown to the right of the title. */
  hint?: string
  /** Extra search terms that don't appear in the title. */
  keywords?: string[]
  /** Section heading in the list. */
  group: 'Navigate' | 'Actions'
  /** The page this command relates to (for page-aware surfacing). */
  page?: PageKey
  /** Route to navigate to when run. */
  to?: string
  /** A side-effecting action id, resolved by the component when run. */
  action?: CommandAction
}

/** Route-prefix → page key. Order doesn't matter; longest-independent prefixes. */
const PATH_PAGE: [string, PageKey][] = [
  ['/home', 'home'],
  ['/briefing', 'briefing'],
  ['/graph', 'constellation'],
  ['/domains', 'domains'],
  ['/pipelines', 'springs'],
  ['/signals', 'signals'],
  ['/ledger', 'ledger'],
  ['/missions', 'missions'],
  ['/agents', 'agents'],
  ['/trove', 'trove'],
  ['/commune', 'commune'],
  ['/settings', 'settings'],
]

/** Map a pathname to the page key its commands should be scoped to. */
export function pageForPath(pathname: string): PageKey {
  for (const [prefix, page] of PATH_PAGE) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return page
  }
  return 'other'
}

// Score bands — a title hit always outranks a keyword-only hit, and a prefix
// always outranks a mid-string substring. The current-page bonus only ever
// breaks ties within a band; it can never lift a weaker match over a stronger.
const SCORE = {
  titlePrefix: 1000,
  titleWord: 800,
  titleSubstring: 600,
  keywordPrefix: 400,
  keywordSubstring: 200,
  pageBonus: 10,
} as const

function scoreText(haystack: string, query: string, prefixScore: number, wordScore: number, subScore: number): number {
  const h = haystack.toLowerCase()
  if (h.startsWith(query)) return prefixScore
  // word-boundary match: query starts one of the whitespace/punctuation-separated words
  if (h.split(/[\s/–—-]+/).some((w) => w.startsWith(query))) return wordScore
  if (h.includes(query)) return subScore
  return 0
}

function scoreCommand(cmd: Command, query: string): number {
  let best = scoreText(cmd.title, query, SCORE.titlePrefix, SCORE.titleWord, SCORE.titleSubstring)
  if (cmd.keywords) {
    for (const kw of cmd.keywords) {
      const s = scoreText(kw, query, SCORE.keywordPrefix, SCORE.keywordPrefix, SCORE.keywordSubstring)
      if (s > best) best = s
    }
  }
  return best
}

/**
 * Filter and rank commands for a query.
 * - Empty query returns all commands, with the current page's commands hoisted
 *   to the front (stable within each group).
 * - Otherwise returns only matching commands, ranked strongest-first, with the
 *   current page breaking ties, then original order as the final tiebreak.
 */
export function filterCommands(commands: Command[], query: string, currentPage?: PageKey): Command[] {
  const q = query.trim().toLowerCase()

  if (q === '') {
    if (!currentPage) return commands.slice()
    // stable partition: current-page commands first, everything else after
    const onPage: Command[] = []
    const rest: Command[] = []
    for (const c of commands) (c.page === currentPage ? onPage : rest).push(c)
    return [...onPage, ...rest]
  }

  const scored = commands
    .map((cmd, index) => ({
      cmd,
      index,
      score: scoreCommand(cmd, q) + (cmd.page && cmd.page === currentPage ? SCORE.pageBonus : 0),
    }))
    .filter((s) => s.score > 0)

  scored.sort((a, b) => b.score - a.score || a.index - b.index)
  return scored.map((s) => s.cmd)
}
